
import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton-loader';
import { usePaymentList } from './hooks/usePaymentList';
import { useAuth } from '@/hooks/useAuth';
import { Family } from '@/types/models';
import { FamilyService } from '@/services/familyServices/familyService';
import PaymentListHeader from './PaymentListHeader';
import PaymentListTable from './PaymentListTable';
import PaymentPagination from './PaymentPagination';
import PaymentFilters from './PaymentFilters';
import ManualPaymentDialog from './ManualPaymentDialog';
import { eventEmitter, EVENTS } from '@/utils/eventEmitter';

const PaymentList = () => {
  const { user } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loadingFamilies, setLoadingFamilies] = useState(false);
  
  const {
    payments,
    loading,
    refreshing,
    page,
    pageSize,
    totalPages,
    totalItems,
    pageSizeOptions,
    isOnline,
    dateRange,
    status,
    paymentMethod,
    handleRefresh,
    handlePageChange,
    handlePageSizeChange,
    handleDateRangeChange,
    handleStatusChange,
    handlePaymentMethodChange,
    handleResetFilters,
  } = usePaymentList();

  // Load families when component mounts
  useEffect(() => {
    const loadFamilies = async () => {
      const organizationId = user?.user_metadata?.organization_id;
      if (!organizationId) return;
      
      setLoadingFamilies(true);
      try {
        const familiesData = await FamilyService.getFamilies(organizationId);
        setFamilies(familiesData);
      } catch (error) {
        console.error('Failed to load families:', error);
      } finally {
        setLoadingFamilies(false);
      }
    };

    loadFamilies();
  }, [user?.user_metadata?.organization_id]);

  const handleRecordPayment = () => {
    setIsDialogOpen(true);
  };

  const handlePaymentCreated = () => {
    handleRefresh();
    // Notify other components (like invoice lists) to refresh their data
    eventEmitter.emit(EVENTS.PAYMENT_RECORDED);
  };

  if (loading && !refreshing) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PaymentListHeader 
        refreshing={refreshing}
        isOnline={isOnline}
        pageSizeOptions={pageSizeOptions}
        pageSize={pageSize}
        onRefresh={handleRefresh}
        onPageSizeChange={handlePageSizeChange}
        onRecordPayment={handleRecordPayment}
      />
      
      <PaymentFilters
        dateRange={dateRange}
        status={status}
        paymentMethod={paymentMethod}
        isLoading={loading || refreshing}
        onDateRangeChange={handleDateRangeChange}
        onStatusChange={handleStatusChange}
        onPaymentMethodChange={handlePaymentMethodChange}
        onResetFilters={handleResetFilters}
      />
      
      {payments.length === 0 && !loading ? (
        <div className="text-center py-10 border rounded-md bg-gray-50">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900">No payment records found</h3>
          <p className="mt-1 text-sm text-gray-500">There are no payment records in the system yet. Click "Record Payment" above to add your first payment.</p>
        </div>
      ) : (
        <>
          <PaymentListTable payments={payments} />
          
          <PaymentPagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            loading={loading}
          />
        </>
      )}
      
      <ManualPaymentDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        families={families}
        organizationId={user?.user_metadata?.organization_id || ''}
        onPaymentCreated={handlePaymentCreated}
      />
    </div>
  );
};

export default PaymentList;
